// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The region dossier — the drill-down every view links into, so a click on a
// polygon, a swarm dot, a matrix row and a trellis tile all land in the same
// place. Shared rather than reimplemented per view, because four slightly
// different dossiers is how the same region ends up with four different
// numbers.

import { closeButton, openOverlay } from '../overlay';
import { getCore, getMeta } from '../data';
import { money, num, pct, signedPct, fy, esc, ha } from '../format';
import { evidenceLines, isRankable, tierPrefix, TIER_WORD, type Tier } from '../uncertainty';
import { FARMING, LAND, CRIMSON, ZONE_COLOUR } from '../theme';
import { chartSvg, el, linearScale, brokenPath, markTip, finite, extent } from './svg';
import { setParams } from '../router';
import type { Region } from '../types';

export function openRegion(name: string): void {
  const core = getCore();
  const meta = getMeta();
  const region = core.regions.find((r) => r.name === name);
  if (!region) return;

  openOverlay({
    panelClass: 'drawer',
    label: `${region.name} — farm economics dossier`,
    lockScroll: true,
    // The deep link is cleared on close so a shared URL does not reopen a
    // drawer the reader has already dismissed.
    onClose: () => setParams({ region: null }),
    build(panel, close) {
      setParams({ region: name });

      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = region.name;
      head.append(h, closeButton(close, `Close the ${region.name} dossier`));

      const body = document.createElement('div');
      body.className = 'stack';

      // ── rank, with its own exclusions disclosed ──────────────────────────
      // Only tier <=2 regions may be ranked. The count that did not qualify is
      // printed rather than quietly dropped, because a leaderboard that hides
      // its own exclusions is a leaderboard that lies about its denominator.
      const rankable = core.regions.filter((r) => isRankable(r.tier as Tier) && r.meanRateEx != null);
      const sorted = [...rankable].sort((a, b) => (b.meanRateEx ?? 0) - (a.meanRateEx ?? 0));
      const rank = sorted.findIndex((r) => r.name === region.name);
      const excluded = core.regions.length - rankable.length;

      const zoneColour = ZONE_COLOUR[region.zone] ?? '#999';
      body.innerHTML = `
        <p class="small muted">
          <span class="pill" style="border-color:${zoneColour}">
            <span class="legend-swatch" style="background:${zoneColour}"></span>${esc(region.zone)} zone
          </span>
          ${region.state ? `<span class="pill">${esc(region.state)}</span>` : ''}
          <span class="pill">${TIER_WORD[region.tier as Tier] ?? '—'} evidence</span>
        </p>
        <div class="stat-row">
          <div class="stat">
            <div class="stat-value farming">${tierPrefix(region.tier as Tier)}${signedPct(region.meanRateEx)}</div>
            <div class="stat-label">a year from farming</div>
            <div class="stat-note">mean ${fy(region.firstYear)} to ${fy(meta.settledLastYear)}, excluding land appreciation</div>
          </div>
          <div class="stat">
            <div class="stat-value land">${tierPrefix(region.tier as Tier)}${signedPct(region.meanRateInc)}</div>
            <div class="stat-label">a year counting the land</div>
            <div class="stat-note">including capital appreciation</div>
          </div>
          <div class="stat">
            <div class="stat-value">${region.landShare == null ? '—' : pct(region.landShare, 0)}</div>
            <div class="stat-label">of the return was the land</div>
            <div class="stat-note">dollar-weighted over the whole period</div>
          </div>
        </div>
        ${rank >= 0 ? `<p class="small muted">Ranks <strong>${rank + 1} of ${rankable.length}</strong> surveyed regions on what the farming itself earned.
          ${excluded > 0 ? `${excluded} of ${core.regions.length} region${excluded === 1 ? ' is' : 's are'} not ranked — their estimates are not reliable enough.` : ''}</p>`
        : `<p class="small muted">Not ranked: this region's estimates are not reliable enough to place against the others.</p>`}
      `;

      // ── the survey behind it, named rather than hidden ───────────────────
      const thin = region.minSample != null && region.minSample < 15;
      const survey = document.createElement('div');
      survey.className = thin ? 'note warn' : 'note';
      const thinYear = region.series.find((s) => s.sample === region.minSample);
      survey.innerHTML = `
        <strong>What is behind these numbers.</strong>
        ABARES surveyed a median of <strong>${num(region.medianSample)}</strong> farms a year in this region,
        with a low of <strong>${num(region.minSample)}</strong>${thinYear ? ` in ${fy(thinYear.year)}` : ''}.
        ${region.weakYears > 0 ? `${region.weakYears} of its ${region.series.length} years carry estimates too weak to read as a figure.` : 'Every year carries a usable estimate.'}
        ${thin ? ' A region-year resting on fewer than 15 farms is treated as unreliable here no matter what its stated error says.' : ''}
      `;
      body.append(survey);

      // ── the two returns, side by side over time ──────────────────────────
      body.append(sparkPanel(region));

      // ── the balance sheet ────────────────────────────────────────────────
      const last = [...region.series].reverse().find((s) => s.year <= meta.settledLastYear && s.landValue != null);
      const first = region.series.find((s) => s.landValue != null);
      if (last && first) {
        const box = document.createElement('div');
        box.className = 'card';
        const mult = first.landValue && last.landValue ? last.landValue / first.landValue : null;
        box.innerHTML = `
          <div class="card-title">The balance sheet, per surveyed farm</div>
          <div class="card-sub">${fy(last.year)}, with the change since ${fy(first.year)}</div>
          <table style="margin-top:.5rem">
            <tbody>
              ${row('Land and fixed improvements', money(last.landValue), mult ? `${mult.toFixed(2)}× since ${fy(first.year)}` : '')}
              ${row('Farm business debt', money(last.debt), first.debt && last.debt ? `${(last.debt / first.debt).toFixed(2)}×` : '')}
              ${row('Equity ratio', pct(last.equityRatio, 0), '')}
              ${row('Total cash receipts', money(last.receipts), first.receipts && last.receipts ? `${(last.receipts / first.receipts).toFixed(2)}×` : '')}
              ${row('Area operated', ha(last.area), first.area && last.area ? `${(last.area / first.area).toFixed(2)}×` : '')}
              ${row('Farms in the region', num(last.population), first.population && last.population ? `${(last.population / first.population).toFixed(2)}×` : '')}
            </tbody>
          </table>
        `;
        body.append(box);
      }

      // ── its zone peers, clickable ────────────────────────────────────────
      const zone = core.zones.find((z) => z.zone === region.zone);
      if (zone) {
        const peers = document.createElement('div');
        peers.className = 'card';
        peers.innerHTML = `<div class="card-title">Its ${esc(region.zone)} peers</div>
          <div class="card-sub">The ${zone.regions} regions ABARES puts in this zone average ${signedPct(zone.meanRateEx)} a year from farming.</div>`;
        const list = document.createElement('div');
        list.className = 'row';
        list.style.flexWrap = 'wrap';
        list.style.marginTop = '.5rem';
        for (const peerName of zone.names) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'pill pill-clear';
          b.textContent = peerName;
          if (peerName === region.name) b.style.background = 'var(--accent-soft)';
          // Every one of these is a live click: it swaps the dossier to that
          // peer rather than being decoration.
          b.addEventListener('click', () => { close(); openRegion(peerName); });
          list.append(b);
        }
        peers.append(list);
        body.append(peers);
      }

      panel.append(head, body);
    },
  });
}

function row(label: string, value: string, note: string): string {
  return `<tr><td>${esc(label)}</td><td class="num">${esc(value)}</td><td class="num muted xsmall">${esc(note)}</td></tr>`;
}

/**
 * The region's two return series over time.
 *
 * Drawn with brokenPath so a year ABARES did not publish leaves a visible gap
 * rather than a straight line across it. The gap between the two lines is the
 * land's contribution, and it is filled so it reads as a quantity.
 */
function sparkPanel(region: Region): HTMLElement {
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `<div class="card-title">What farming earned, and what the land added</div>
    <div class="card-sub">Annual rate of return, ${fy(region.firstYear)} onward. The shaded band is the land's contribution.</div>`;

  const W = 520; const H = 190;
  const m = { t: 12, r: 12, b: 28, l: 44 };
  const years = region.series.map((s) => s.year);
  const vals = [...region.series.map((s) => s.rateEx), ...region.series.map((s) => s.rateInc)];
  const [lo, hi] = extent(vals);
  const x = linearScale([Math.min(...years), Math.max(...years)], [m.l, W - m.r]);
  const y = linearScale([Math.min(lo, 0) * 1.1, Math.max(hi, 1) * 1.1], [H - m.b, m.t]);

  const svg = chartSvg(W, H, `${region.name}: annual rate of return with and without land appreciation`);

  // zero rule — the line between farming that pays for its capital and farming that does not
  svg.append(el('line', { class: 'zero-line', x1: m.l, y1: finite(y(0)), x2: W - m.r, y2: finite(y(0)) }));

  // The band between the two series, only across runs where BOTH exist.
  const bandRuns: Array<Array<{ x: number; ex: number; inc: number }>> = [];
  let run: Array<{ x: number; ex: number; inc: number }> = [];
  for (const s of region.series) {
    if (s.rateEx == null || s.rateInc == null) { if (run.length > 1) bandRuns.push(run); run = []; continue; }
    run.push({ x: finite(x(s.year)), ex: finite(y(s.rateEx)), inc: finite(y(s.rateInc)) });
  }
  if (run.length > 1) bandRuns.push(run);
  for (const r of bandRuns) {
    const top = r.map((p) => `${p.x.toFixed(1)},${p.inc.toFixed(1)}`).join(' L');
    const bottom = [...r].reverse().map((p) => `${p.x.toFixed(1)},${p.ex.toFixed(1)}`).join(' L');
    svg.append(el('path', { d: `M${top} L${bottom} Z`, fill: LAND, opacity: 0.22 }));
  }

  for (const [key, colour] of [['rateEx', FARMING], ['rateInc', LAND]] as const) {
    const pts = region.series.map((s) => ({ x: finite(x(s.year)), y: s[key] == null ? null : finite(y(s[key] as number)) }));
    for (const d of brokenPath(pts)) {
      svg.append(el('path', { d, fill: 'none', stroke: colour, 'stroke-width': 1.8, 'stroke-linejoin': 'round' }));
    }
  }

  // One hoverable dot per year, carrying the evidence lines.
  for (const s of region.series) {
    if (s.rateEx == null) continue;
    const dot = el('circle', {
      cx: finite(x(s.year)), cy: finite(y(s.rateEx)), r: 2.6,
      fill: (s.rateEx ?? 0) < 0 ? CRIMSON : FARMING, class: 'mark',
    });
    markTip(dot, [
      `${region.name}, ${fy(s.year)}`,
      `Farming: ${tierPrefix(s.tier as Tier)}${signedPct(s.rateEx)}`,
      `Counting the land: ${signedPct(s.rateInc)}`,
      ...evidenceLines(s.sample, s.tier as Tier, s.rateExRse),
    ]);
    svg.append(dot);
  }

  const ticks = years.filter((yr) => yr % 10 === 0);
  for (const t of ticks) {
    const lab = el('text', { class: 'axis-text', x: finite(x(t)), y: H - 8, 'text-anchor': 'middle' });
    lab.textContent = String(t);
    svg.append(lab);
  }
  for (const t of y.ticks(4)) {
    const lab = el('text', { class: 'axis-text', x: m.l - 6, y: finite(y(t)) + 4, 'text-anchor': 'end' });
    lab.textContent = `${t.toFixed(0)}%`;
    svg.append(lab);
  }

  const wrap = document.createElement('div');
  wrap.className = 'chart-scroll';
  wrap.append(svg);
  card.append(wrap);

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = `
    <span class="legend-item"><span class="legend-swatch" style="background:${FARMING}"></span>what the farming earned</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${LAND}"></span>counting land appreciation too</span>`;
  card.append(legend);
  return card;
}
